import React from 'react';
import ReactSelect, { ActionMeta, ValueType } from 'react-select';

import { Box, GridContainer, Sizes } from '../../common/grid-container/grid-container';
import classnames from 'classnames';

const fonts = require('../../common/fonts.css');
const styles = require('./styles.module.css');

export interface SelectOption {
  value: React.ReactText;
  label: string;
}

interface SelectProps {
  options: SelectOption[];
  onChange?(value?: SelectOption): void;
  autoFocus?: boolean;
  defaultValue?: SelectOption;
  value?: SelectOption;
  isDisabled?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  // We are/are not interested when the selection is cleared
  clearSelection?: boolean;
}

interface MultiSelectProps {
  options: SelectOption[];
  onChange?(value?: SelectOption[]): void;
  autoFocus?: boolean;
  defaultValue?: SelectOption[];
  isDisabled?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  clearSelection?: boolean;
}
const defaultStyles = {
  // tslint:disable: no-any
  option: (provided: any, state: { isSelected: boolean; isFocused: boolean }) => ({
    ...provided,
    color: state.isSelected ? 'white' : 'var(--shade-dark)',
    backgroundColor: state.isSelected
      ? 'var(--primary-orange)'
      : state.isFocused
        ? 'var(--secondary-rag-amber)'
        : 'white'
  }),
  control: (provided: any, state: { isFocused: boolean }) => ({
    ...provided,
    // marginTop: "5",
    borderRadius: '0',
    fontSize: '12px',
    lineHeight: '20px',
    borderColor: state.isFocused ? 'var(--primary-orange)' : 'var(--shade-medium)',
    outline: state.isFocused && 'none'
  }),
  menu: (styles: any) => ({
    ...styles,
    zIndex: 999,
    fontSize: '12px',
    lineHeight: '20px'
  }),
  container: (provided: any, state: { isFocused: boolean }) => ({
    ...provided,
    borderColor: state.isFocused ? 'var(--primary-orange)' : 'var(--shade-medium)',
    outline: state.isFocused && 'none',
    padding: '5px'
  }),
  placeholder: (provided: any, state: { isFocused: boolean; value: any }) => ({
    ...provided,
    maxWidth: 'none',
    ...(state.isFocused && state.value
      ? {}
      : {
        position: 'static',
        top: 'auto',
        transform: 'none'
      })
  }),
  singleValue: (provided: any, state: { isFocused: boolean; value: any }) => ({
    ...provided,
    maxWidth: 'none',
    ...(state.isFocused && state.value
      ? {}
      : {
        position: 'static',
        top: 'auto',
        transform: 'none'
      })
  })
};

export class Select extends React.Component<SelectProps> {
  onChange = (option: ValueType<SelectOption>, action: ActionMeta<SelectOption>) => {
    const { clearSelection, onChange } = this.props;
    if (onChange) {
      if (option) {
        onChange(option as SelectOption);
      } else if (clearSelection && action.action === 'clear') {
        onChange();
      }
    }
  };

  render() {
    const { autoFocus, defaultValue, isClearable, isDisabled, options, placeholder, value } = this.props;
    const styles = isDisabled
      ? {
        ...defaultStyles,
        // New Admin UI: Ensure these components are as styled whwn the MultiSelect
        // or Select boxes are disabled. See https://react-select.com/components
        indicatorsContainer: (provided: any, state: { isDisabled: boolean; isLoading: boolean }) => ({
          ...provided,
          display: state.isDisabled && !state.isLoading && 'none'
        }),
        control: (provided: any, state: { isDisabled: boolean }) => ({
          ...provided,
          border: state.isDisabled && 'none',
          fontSize: state.isDisabled && '85%'
        })
      }
      : defaultStyles;
    return (
      <ReactSelect
        styles={styles}
        options={options}
        onChange={this.onChange}
        autoFocus={autoFocus}
        defaultValue={defaultValue}
        value={value || null}
        menuPlacement={'auto'}
        menuPosition={'fixed'}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isClearable={isClearable}
      />
    );
  }
}

interface SelectBoxProps extends SelectProps {
  xsSpan: Sizes['xsSpan'];
  title?: string;
  isMandatory?: boolean;
  debug?: boolean;
}

export class SelectBox extends React.Component<SelectBoxProps> {
  render() {
    const { debug, defaultValue, isMandatory, title, xsSpan, } = this.props;
    return (
      <GridContainer debug={debug} xsSpan={xsSpan}>
        <Box>
          {title && <p className={classnames(fonts.caption, isMandatory && styles.mandatory)}>{title}</p>}
          <Select {...this.props} />
          {debug && (
            <div>Current value:{defaultValue === undefined ? undefined : defaultValue.label}</div>
          )}
        </Box>
      </GridContainer>
    );
  }
}

export class MultiSelect extends React.Component<MultiSelectProps> {
  onChange = (option: ValueType<SelectOption>, action: ActionMeta<SelectOption>) => {
    const { clearSelection, onChange } = this.props;
    if (onChange) {
      if (option) {
        onChange(option as SelectOption[]);
      } else if (clearSelection && action.action === 'clear') {
        onChange();
        /// Just so happens that if 'clear' is used, then all items are deleted.
        /// If there are more than one to start with and one is deleted, this is
        /// also detected. *However* if the list is emptied by just deleting the
        /// last remaining option, then 'option' is undefined here and onChange
        /// won't fire. Fix should work because we are automatically casting to
        /// an array
      } else {
        const empty: SelectOption[] = [];
        onChange(empty);
      }
    }
  };

  render() {
    const { autoFocus, defaultValue, isClearable, isDisabled, options, placeholder } = this.props;
    const styles = isDisabled
      ? {
        ...defaultStyles,
        /// New Admin UI: Ensure these components are as styled whwn the MultiSelect
        /// or Select boxes are disabled. MultiValue has to add padding to compensate
        /// for missing remove icon. See https://react-select.com/components
        control: (provided: any, state: { isDisabled: boolean }) => ({
          ...provided,
          border: state.isDisabled && 'none',
          fontSize: state.isDisabled && '85%'
        }),
        multiValue: (provided: any, state: { isDisabled: boolean }) => ({
          ...provided,
          paddingRight: state.isDisabled && '3px'
        }),
        multiValueRemove: (provided: any, state: { isDisabled: boolean }) => ({
          ...provided,
          display: state.isDisabled && 'none'
        }),
        indicatorsContainer: (provided: any, state: { isDisabled: boolean; isLoading: boolean }) => ({
          ...provided,
          display: state.isDisabled && !state.isLoading && 'none'
        })
      }
      : defaultStyles;
    return (
      <ReactSelect
        styles={styles}
        options={options}
        onChange={this.onChange}
        autoFocus={autoFocus}
        defaultValue={defaultValue}
        menuPlacement={'auto'}
        menuPosition={'fixed'}
        isMulti
        isDisabled={isDisabled}
        isClearable={isClearable}
        placeholder={placeholder}
      />
    );
  }
}

interface MultiSelectBoxProps extends MultiSelectProps {
  xsSpan: Sizes['xsSpan'];
  title: string;
  debug?: boolean;
}

export class MultiSelectBox extends React.Component<MultiSelectBoxProps> {
  render() {
    return (
      <GridContainer debug={this.props.debug} xsSpan={this.props.xsSpan}>
        <Box>
          <p className={fonts.caption}>{this.props.title}</p>
          <MultiSelect {...this.props} />
          {this.props.debug && (
            <div>
              Current value:
              {this.props.defaultValue === undefined
                ? undefined
                : this.props.defaultValue.map(option => option.label).join()}
            </div>
          )}
        </Box>
      </GridContainer>
    );
  }
}



// WEBPACK FOOTER //
// ./src/ui/widgets/select/select.tsx